import { z } from 'zod';
export const StoredFileOriginSchema = z.enum(['user', 'workflow']);
export const StoredFileUploadStatusSchema = z.enum(['pending', 'completed', 'failed']);
export const StoredFileSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    path: z.string(),
    size: z.number().int().nonnegative(),
    contentType: z.string(),
    origin: StoredFileOriginSchema,
    uploadStatus: StoredFileUploadStatusSchema,
    uploadId: z.string().optional(),
    description: z.string().optional(),
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().optional()
});
export const StoredFileDetailSchema = StoredFileSchema.extend({
    downloadUrl: z.url().optional()
});
/**
 * Payload of a `data-file` message part — a reference to a conversation file
 * (a StoredFile). Direction is carried by the containing message's role: in an
 * assistant message it is a file the agent produced; in a user message it is a
 * file the user attached. The part never embeds a URL — presigned download URLs
 * are resolved on demand, outside the message flow.
 */
export const DataFilePartDataSchema = z.object({
    origin: StoredFileOriginSchema,
    path: z.string().min(1),
    size: z.number().int().nonnegative().optional()
});
export const PresignedUrlPartSchema = z.object({
    partNumber: z.number().int().positive(),
    url: z.url(),
    size: z.number().int().positive()
});
export const InitiateUploadRequestSchema = z.object({
    path: z.string().min(1, 'Path is required'),
    size: z.number().int().positive('Size must be a positive number'),
    contentType: z.string().optional()
});
export const InitiateUploadResponseSchema = StoredFileSchema.extend({
    presignedUrls: z.array(PresignedUrlPartSchema)
});
export const CompleteUploadRequestSchema = z.object({
    parts: z.array(z.object({
        partNumber: z.number().int().positive(),
        etag: z.string()
    })).min(1, 'Parts array is required and must not be empty')
});
// Note: the legacy `s3` wrapper (containing etag) is intentionally omitted from the wire
// format — the front never consumed it.
export const CompleteUploadResponseSchema = StoredFileSchema;
/**
 * Canonical form an upload path is stored under. Shared by the front (so an
 * optimistic attachment ref matches the StoredFile the server creates) and the
 * back (validateAndNormalizePath applies this, then validates). It runs NFC and
 * folds characters an LLM does not retype faithfully when calling tools:
 * typographic quotes -> ASCII, exotic spaces -> a regular space. Accents are
 * kept (models reproduce them reliably). Code points are used (not unicode
 * literals) to keep the source unambiguous. Idempotent.
 */
export function normalizeUploadPath(originalPath) {
    let out = '';
    for (const ch of originalPath.normalize('NFC')) {
        const cp = ch.codePointAt(0);
        if (cp === 0x20_18 || cp === 0x20_19 || cp === 0x20_1B || cp === 0x20_32) {
            out += '\''; // curly single quotes / prime -> '
        }
        else if (cp === 0x20_1C || cp === 0x20_1D || cp === 0x20_1E || cp === 0x20_33) {
            out += '"'; // curly double quotes / double prime -> "
        }
        else if (cp === 0x00_A0 || (cp >= 0x20_00 && cp <= 0x20_0A) || cp === 0x20_2F || cp === 0x20_5F || cp === 0x30_00) {
            out += ' '; // exotic spaces -> regular space
        }
        else {
            out += ch;
        }
    }
    return out;
}
